import * as React from 'react';
import * as styled from './styled';
import {Thumbnail} from './styled';
import {useFormikContext} from 'formik';
import {ExerciseObject} from 'api/exercise/types';
import {getExercise} from 'api/exercise/requests';
import {getImageByPreview} from 'api/library';
import {ProgramItem, ProgramObject, TrainingItem} from 'api/program/types';
import ControlButton from 'components/ControlButton';
import Input, {InputVariant} from 'components/Input';
import useLoading from 'hooks/use-loading';
import validateResponse from 'helpers/validate-response';
import isError from 'helpers/isError';
import {ExerciseProps} from './types';
import {IconTrash} from 'icons';
import Select, {SelectVariant} from '../../../../../Select';

/**
 * The exercise card.
 * @param {ExerciseProps} props
 * @return {React.ReactElement<ExerciseProps>}
 * @constructor
 */
function Exercise(
    props: ExerciseProps,
): React.ReactElement<ExerciseProps> {
  const {
    values, setFieldValue,
  } = useFormikContext<Partial<ProgramObject<TrainingItem>>>();

  const {
    data, fetch, render,
  } = useLoading<ExerciseObject>();

  const exercises: ProgramItem<TrainingItem>[]
    = values?.days && values?.days[0].items || [];

  React.useEffect(() => {
    fetch(() => new Promise((resolve) => (
      getExercise(props.id).then((response) => {
        validateResponse(response);

        if (response && !isError(response)) {
          resolve(response);
        }
      })
    )))
  }, [props.id]);

  /**
   * Render content.
   * @return {JSX.Element}
   */
  function renderContent(): JSX.Element {
    /**
     * Render until.
     * @return {JSX.Element}
     */
    function renderUntil(): JSX.Element {
      return (
        <styled.InputContainer>
          <Select
            components={{
              DropdownIndicator: () => null,
              IndicatorSeparator: () => null,
            }}
            isSearchable={false}
            variant={SelectVariant.Accent}
            defaultValue={{
              label: 'раз',
              value: 'count',
            }}
            options={[
              {
                label: 'раз',
                value: 'count',
              },
              {
                label: 'сек',
                value: 'seconds',
              },
              {
                label: 'мин',
                value: 'minutes',
              },
            ]}
          />
        </styled.InputContainer>
      );
    }

    /**
     * Render thumbnail.
     * @return {JSX.Element}
     */
    function renderThumbnail(): JSX.Element {
      return (
        <Thumbnail
          src={getImageByPreview(data?.preview)}
          alt="Изображение упражнения"
        />
      );
    }

    /**
     * Render title.
     * @return {JSX.Element}
     */
    function renderTitle(): JSX.Element {
      return (
        <styled.Title>
          {data?.title}
        </styled.Title>
      );
    }

    /**
     * Render approaches.
     * @return {JSX.Element}
     */
    function renderApproaches(): JSX.Element {
      /**
       * Render count.
       * @return {JSX.Element}
       */
      function renderCount(): JSX.Element {
        return (
          <styled.InputContainer>
            <Input
              type="number"
              defaultValue={0}
              variant={InputVariant.Accent}
              stretched
            />
          </styled.InputContainer>
        );
      }

      /**
       * Render amount.
       * @return {JSX.Element}
       */
      function renderAmount(): JSX.Element {
        return (
          <styled.InputContainer>
            <Input
              type="number"
              defaultValue={0}
              variant={InputVariant.Accent}
              stretched
            />
          </styled.InputContainer>
        );
      }

      return (
        <styled.Approaches>
          <styled.Container>
            {renderCount()}
            <styled.Label>
              подходов
            </styled.Label>
            <styled.Label>
              по
            </styled.Label>
            {renderAmount()}
            {renderUntil()}
          </styled.Container>
        </styled.Approaches>
      );
    }

    /**
     * Render rest.
     * @return {JSX.Element}
     */
    function renderRest(): JSX.Element {
      /**
       * Render amount.
       * @return {JSX.Element}
       */
      function renderAmount(): JSX.Element {
        return (
          <styled.InputContainer>
            <Input
              type="number"
              defaultValue={0}
              variant={InputVariant.Accent}
              stretched
            />
          </styled.InputContainer>
        );
      }

      return (
        <styled.Rest>
          <styled.Container>
            <styled.Label>
              Отдых между подходами
            </styled.Label>
            {renderAmount()}
            {renderUntil()}
          </styled.Container>
        </styled.Rest>
      );
    }

    /**
     * Render controls.
     * @return {JSX.Element}
     */
    function renderControls(): JSX.Element {
      /**
       * Render delete control.
       * @return {JSX.Element}
       */
      function renderDelete(): JSX.Element {
        /**
         * The click event handler.
         * @return {void}
         */
        function handleClick(): void {
          setFieldValue(`days[0].items`, exercises.filter((exercise, index) => (
            props.index !== index
          )));
        }

        return (
          <ControlButton type="button" onClick={handleClick}>
            <IconTrash />
          </ControlButton>
        );
      }

      return (
        <styled.Controls>
          {renderDelete()}
        </styled.Controls>
      );
    }

    return (
      <styled.Wrapper>
        {renderThumbnail()}
        {renderTitle()}
        {renderControls()}
        {renderApproaches()}
        {renderRest()}
      </styled.Wrapper>
    );
  }

  /**
   * Render not found.
   * @return {JSX.Element}
   */
  function renderNotFound(): JSX.Element {
    return (
      <React.Fragment />
    );
  }

  return render(renderContent, renderNotFound);
}

export default React.memo(Exercise);
